import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "./ui/alert-dialog";

interface SurveyTemplate {
    id: string;
    name: string;
    description?: string;
    type: 'CSAT' | 'NPS';
    config: {
        options?: string[];
        scaleLabelLow?: string;
        scaleLabelHigh?: string;
        thankYouMessage: string;
    };
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

interface SurveyTemplateManagerProps {
    tenantId?: string;
}

export function SurveyTemplateManager({ tenantId }: SurveyTemplateManagerProps) {
    const [templates, setTemplates] = useState<SurveyTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<SurveyTemplate | null>(null);
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
        type?: 'danger' | 'info';
        confirmText?: string;
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'info'
    });

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        type: 'CSAT' as 'CSAT' | 'NPS',
        config: {
            scaleLabelLow: 'Muito Insatisfeito',
            scaleLabelHigh: 'Muito Satisfeito',
            thankYouMessage: 'Obrigado por sua resposta!'
        }
    });

    const showConfirm = (config: {
        title: string;
        message: string;
        onConfirm: () => void;
        type?: 'danger' | 'info';
        confirmText?: string;
    }) => {
        setConfirmDialog({
            isOpen: true,
            ...config
        });
    };

    const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
        const token = localStorage.getItem('auth_token');
        const selectedTenantId = localStorage.getItem('selected_tenant_id') || tenantId;
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (token) {
            (headers as Record<string, string>).Authorization = `Bearer ${token}`;
        }

        if (selectedTenantId) {
            (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
        }

        return fetch(url, { ...options, headers });
    }, [tenantId]);

    const loadTemplates = useCallback(async () => {
        try {
            const response = await authenticatedFetch('/api/surveys/templates');
            if (response.ok) {
                const data = await response.json();
                setTemplates(data || []);
            }
        } catch (error) {
            console.error('Erro ao carregar templates:', error);
            toast.error('Erro ao carregar templates de pesquisa');
        } finally {
            setLoading(false);
        }
    }, [authenticatedFetch]);

    useEffect(() => {
        loadTemplates();
    }, [loadTemplates]);

    const handleCreateOrUpdate = async () => {
        if (!formData.name) {
            toast.error('Preencha o campo obrigatório (Nome)');
            return;
        }

        try {
            const url = selectedTemplate 
                ? `/api/surveys/templates/${selectedTemplate.id}`
                : '/api/surveys/templates';
            
            const method = selectedTemplate ? 'PUT' : 'POST';

            const response = await authenticatedFetch(url, {
                method,
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                toast.success(selectedTemplate ? 'Template atualizado!' : 'Template criado!');
                setIsModalOpen(false);
                resetForm();
                loadTemplates();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Erro ao salvar template');
            }
        } catch (error) {
            console.error('Erro ao salvar template:', error);
            toast.error('Erro ao salvar template');
        }
    };

    const handleDelete = async (id: string) => {
        showConfirm({
            title: 'Excluir Template',
            message: 'Tem certeza que deseja excluir este template de pesquisa?',
            confirmText: 'Excluir',
            type: 'danger',
            onConfirm: async () => {
                try {
                    const response = await authenticatedFetch(`/api/surveys/templates/${id}`, {
                        method: 'DELETE'
                    });

                    if (response.ok) {
                        toast.success('Template excluído!');
                        loadTemplates();
                    } else {
                        const error = await response.json();
                        toast.error(error.error || 'Erro ao excluir template');
                    }
                } catch (error) {
                    console.error('Erro ao excluir template:', error);
                    toast.error('Erro ao excluir template');
                }
            }
        });
    };

    const resetForm = () => {
        setSelectedTemplate(null);
        setFormData({
            name: '',
            description: '',
            type: 'CSAT',
            config: {
                scaleLabelLow: 'Muito Insatisfeito',
                scaleLabelHigh: 'Muito Satisfeito',
                thankYouMessage: 'Obrigado por sua resposta!'
            }
        });
    };

    const openEditModal = (template: SurveyTemplate) => {
        setSelectedTemplate(template);
        setFormData({
            name: template.name,
            description: template.description || '',
            type: template.type,
            config: {
                scaleLabelLow: template.config.scaleLabelLow || 'Muito Insatisfeito',
                scaleLabelHigh: template.config.scaleLabelHigh || 'Muito Satisfeito',
                thankYouMessage: template.config.thankYouMessage || 'Obrigado por sua resposta!'
            }
        });
        setIsModalOpen(true);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
                <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
                <div className="text-gray-400 font-oswald uppercase tracking-[0.2em] font-bold text-sm">Carregando templates...</div>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fadeIn">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white/5 p-8 rounded-[2rem] border border-white/10 backdrop-blur-md relative overflow-hidden group/header">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-transparent opacity-0 group-hover/header:opacity-100 transition-opacity" />
                <div className="relative z-10">
                    <h2 className="text-3xl font-bold font-oswald text-white uppercase tracking-tight">Templates de Pesquisa</h2>
                    <p className="text-[10px] font-bold text-gray-300 mt-2 uppercase tracking-[0.2em] font-oswald">Gerencie seus templates de CSAT e NPS</p>
                </div>
                <button
                    onClick={() => { resetForm(); setIsModalOpen(true); }}
                    className="relative z-10 px-8 py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] text-xs hover:bg-blue-500 shadow-lg shadow-blue-900/20 hover:shadow-blue-500/40 hover:-translate-y-1 transition-all duration-300 flex items-center gap-3 group/btn overflow-hidden border border-blue-400/50"
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                    <span className="text-lg group-hover:rotate-12 transition-transform">📊</span> Novo Template
                </button>
            </div>

            {/* Templates List */}
            {templates.length === 0 ? (
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[3rem] p-20 text-center relative overflow-hidden group/empty">
                    <div className="absolute inset-0 bg-gradient-to-b from-blue-600/5 to-transparent opacity-0 group-hover/empty:opacity-100 transition-opacity" />
                    <div className="w-24 h-24 bg-white/5 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-white/10 shadow-inner relative z-10">
                        <span className="text-5xl opacity-50 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-500">📊</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4 font-oswald uppercase tracking-tight relative z-10">Nenhum template de pesquisa</h3>
                    <p className="text-gray-300 mb-10 max-w-md mx-auto font-oswald text-[10px] font-bold uppercase tracking-[0.2em] leading-relaxed relative z-10">
                        Crie templates de CSAT ou NPS para coletar feedback de seus clientes automaticamente via WhatsApp
                    </p>
                    <button
                        onClick={() => { resetForm(); setIsModalOpen(true); }}
                        className="relative z-10 px-10 py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] text-xs hover:bg-blue-500 shadow-xl shadow-blue-900/40 hover:-translate-y-1 transition-all group/btn overflow-hidden border border-blue-400/50"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                        Criar Primeiro Template
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map((template) => (
                        <div key={template.id} className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] p-6 hover:bg-white/[0.08] transition-all group relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-4">
                                <span className={`px-3 py-1 rounded-full text-[10px] font-bold font-oswald uppercase tracking-wider ${
                                    template.type === 'CSAT' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                                }`}>
                                    {template.type}
                                </span>
                            </div>
                            <h3 className="text-xl font-bold text-white font-oswald uppercase tracking-wide mb-2 mt-4">{template.name}</h3>
                            <p className="text-gray-400 text-sm mb-6 line-clamp-2 h-10">{template.description || 'Sem descrição'}</p>
                            
                            <div className="flex items-center justify-between pt-6 border-t border-white/5">
                                <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest font-oswald">
                                    {new Date(template.updatedAt).toLocaleDateString('pt-BR')}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => openEditModal(template)}
                                        className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors border border-blue-500/20"
                                        title="Editar"
                                    >
                                        ✏️
                                    </button>
                                    <button
                                        onClick={() => handleDelete(template.id)}
                                        className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors border border-red-500/20"
                                        title="Excluir"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-[#111] border border-white/10 rounded-[2.5rem] w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
                        <div className="p-8 border-b border-white/5 flex justify-between items-center bg-white/5">
                            <div>
                                <h2 className="text-2xl font-bold text-white font-oswald uppercase tracking-tight">
                                    {selectedTemplate ? 'Editar Template' : 'Novo Template de Pesquisa'}
                                </h2>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald mt-1">Configure os detalhes da sua pesquisa</p>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white transition-colors text-2xl">✕</button>
                        </div>
                        
                        <div className="p-8 overflow-y-auto custom-scrollbar space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Nome do Template *</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Ex: Pesquisa de Satisfação Pós-Venda"
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white font-oswald uppercase tracking-wider text-xs focus:border-blue-500/50 outline-none transition-all"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Tipo de Pesquisa</label>
                                    <select
                                        value={formData.type}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value as 'CSAT' | 'NPS' })}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white font-oswald uppercase tracking-wider text-xs focus:border-blue-500/50 outline-none transition-all"
                                    >
                                        <option value="CSAT">CSAT (1-5)</option>
                                        <option value="NPS">NPS (0-10)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Descrição</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Uma breve descrição sobre este template..."
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white font-oswald uppercase tracking-wider text-xs focus:border-blue-500/50 outline-none transition-all h-24 resize-none"
                                />
                            </div>

                            <div className="p-6 bg-blue-500/5 border border-blue-500/20 rounded-3xl space-y-6">
                                <h3 className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] font-oswald flex items-center gap-2">
                                    <span>⚙️</span> Configuração da Mensagem
                                </h3>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Legenda Baixa (1/0)</label>
                                        <input
                                            type="text"
                                            value={formData.config.scaleLabelLow}
                                            onChange={(e) => setFormData({ ...formData, config: { ...formData.config, scaleLabelLow: e.target.value } })}
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white font-oswald uppercase tracking-wider text-xs focus:border-blue-500/50 outline-none transition-all"
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Legenda Alta (5/10)</label>
                                        <input
                                            type="text"
                                            value={formData.config.scaleLabelHigh}
                                            onChange={(e) => setFormData({ ...formData, config: { ...formData.config, scaleLabelHigh: e.target.value } })}
                                            className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white font-oswald uppercase tracking-wider text-xs focus:border-blue-500/50 outline-none transition-all"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald ml-1">Mensagem de Agradecimento</label>
                                    <input
                                        type="text"
                                        value={formData.config.thankYouMessage}
                                        onChange={(e) => setFormData({ ...formData, config: { ...formData.config, thankYouMessage: e.target.value } })}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white font-oswald uppercase tracking-wider text-xs focus:border-blue-500/50 outline-none transition-all"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="p-8 border-t border-white/5 flex justify-end gap-4 bg-white/5">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="px-8 py-4 bg-white/5 text-gray-400 rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] text-[10px] hover:bg-white/10 transition-all border border-white/10"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleCreateOrUpdate}
                                className="px-10 py-4 bg-blue-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] text-[10px] hover:bg-blue-500 shadow-xl shadow-blue-900/40 transition-all border border-blue-400/50"
                            >
                                {selectedTemplate ? 'Salvar Alterações' : 'Criar Template'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Dialog */}
            <AlertDialog open={confirmDialog.isOpen} onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}>
                <AlertDialogContent className="bg-[#111] border border-white/10 rounded-[2.5rem] p-8">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-2xl font-bold text-white font-oswald uppercase tracking-tight">
                            {confirmDialog.title}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-400 font-oswald text-[10px] font-bold uppercase tracking-[0.2em] mt-4 leading-relaxed">
                            {confirmDialog.message}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-8 gap-4">
                        <AlertDialogCancel className="bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] text-[10px] px-8 py-4">
                            Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDialog.onConfirm}
                            className={`${confirmDialog.type === 'danger' ? 'bg-red-600 hover:bg-red-500' : 'bg-blue-600 hover:bg-blue-500'} text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] text-[10px] px-10 py-4 border-none shadow-xl`}
                        >
                            {confirmDialog.confirmText || 'Confirmar'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
